import { format, isValid } from 'date-fns'

const normalizeUtcString = (value?: string) => {
  if (!value) return undefined
  const withT = value.replace(' ', 'T')
  // These are UTC timestamps. A TZ-less datetime (e.g. "2024-06-15T12:00:00")
  // is parsed as *local* time by `new Date`, so force UTC by appending 'Z' to
  // match the prior moment.utc() behavior. Leave date-only or already-zoned
  // strings untouched.
  const hasTime = /T\d{2}:\d{2}/.test(withT)
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(withT)
  return hasTime && !hasZone ? `${withT}Z` : withT
}

export const utcStringToLocalDate = (value?: string): Date | null => {
  if (!value) return null
  const normalized = normalizeUtcString(value)
  if (!normalized) return null
  const date = new Date(normalized)
  if (!isValid(date)) return null
  return date
}

export const formatUtcDateTime = (value?: string) => {
  const date = utcStringToLocalDate(value)
  if (!date) return { date: '—', time: '' }
  return {
    date: format(date, 'MMM d, yyyy'),
    time: format(date, 'h:mm a'),
  }
}

export const formatUtcDateLabel = (value?: string) => {
  const { date } = formatUtcDateTime(value)
  return date === '—' ? '' : date
}

export const utcStringToTimestamp = (value?: string) => utcStringToLocalDate(value)?.getTime() ?? 0

export const formatUtcMonthYear = (value?: string) => {
  const date = utcStringToLocalDate(value)
  if (!date) return ''
  return format(date, 'MMMM yyyy')
}

export const isTodayUtc = (value?: string) => {
  const date = utcStringToLocalDate(value)
  if (!date) return false
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

export const isThisWeekUtc = (value?: string) => {
  const date = utcStringToLocalDate(value)
  if (!date) return false
  const now = new Date()
  const dayOfWeek = now.getDay()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  start.setDate(now.getDate() - dayOfWeek)
  const end = new Date(start)
  end.setDate(start.getDate() + 7)
  return date >= start && date < end
}

export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export const getTimeZoneOptions = () => {
  const current = getBrowserTimeZone()
  let zones: string[] = []
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      zones = Intl.supportedValuesOf('timeZone')
    }
  } catch {
    // intentional
  }
  if (!zones.length) zones = [current]
  const unique = Array.from(new Set(zones))
  const sorted = unique.filter((z) => z !== current).sort((a, b) => a.localeCompare(b))
  return [current, ...sorted]
}
